import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api.js";
import { Skeleton } from "@templestay/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { nameI18nCompletion } from "@/lib/i18nCompletion.js";
import { CompletionBadge } from "@/components/CompletionBadge.js";
import type { Test } from "@templestay/db";
import { useTenantPath, useTenantPublicBase } from "@/lib/hooks.js";
import { useAuthStore } from "@/store/auth.js";

export function TestsPage() {
  const tenantPath = useTenantPath();
  const PUBLIC_BASE = useTenantPublicBase();
  const { activeTenantId } = useAuthStore();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Test | null>(null);
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);

  const { data: tests, isLoading } = useQuery({
    queryKey: ["tests", activeTenantId],
    queryFn: () => api.get<Test[]>("/api/tests"),
    enabled: !!activeTenantId,
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => api.post<Test>("/api/tests", { name, collect_personal_info: true }),
    onSuccess: () => { setCreating(false); setNewName(""); qc.invalidateQueries({ queryKey: ["tests"] }); },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/api/tests/${id}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tests"] }),
    onError: (e) => console.error("activate error:", e),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/tests/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tests"] }); setDeleteTarget(null); },
  });

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* ── Left: list ───────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">검사</h1>
          <button onClick={() => setCreating(true)} className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600">
            + 생성
          </button>
        </div>

        {creating && (
          <div className="mb-4 flex gap-2 rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
            <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createMutation.mutate(newName)}
              placeholder="검사 이름" className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400" />
            <button onClick={() => createMutation.mutate(newName)} disabled={!newName.trim() || createMutation.isPending}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">만들기</button>
            <button onClick={() => setCreating(false)} className="rounded-lg px-3 py-2 text-sm text-gray-500 hover:bg-gray-50">취소</button>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
        ) : !tests?.length ? (
          <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-black/5">
            <p className="text-gray-400">아직 검사가 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tests.map((test) => (
              <div key={test.id} onClick={() => setSelectedTest(test)}
                className={`flex cursor-pointer items-center gap-4 rounded-xl bg-white px-5 py-4 shadow-sm ring-1 transition-colors ${selectedTest?.id === test.id ? "ring-brand-400 bg-brand-50/30" : "ring-black/5 hover:ring-brand-200"}`}>
                <div className="flex-1 min-w-0">
                  <p className="truncate font-semibold text-gray-900">{test.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-gray-400">{new Date(test.updated_at).toLocaleString("ko-KR")}</p>
                    <span className="text-xs text-gray-300 font-mono">{test.slug}</span>
                    {test.name_i18n && (
                      <CompletionBadge pct={nameI18nCompletion(test.name_i18n)} />
                    )}
                  </div>
                </div>
                {test.is_active ? (
                  <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">액티브</span>
                ) : (
                  <button onClick={(e) => { e.stopPropagation(); activateMutation.mutate(test.id); }} disabled={activateMutation.isPending}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">액티브로</button>
                )}
                <Link to={tenantPath(`tests/${test.id}/edit`)} onClick={(e) => e.stopPropagation()}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">편집</Link>
                <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(test); }}
                  className="rounded-lg px-2 py-1.5 text-xs text-red-400 hover:bg-red-50 hover:text-red-600">삭제</button>
              </div>
            ))}
          </div>
        )}

        <ConfirmDialog open={!!deleteTarget} title="검사 삭제"
          description={`"${deleteTarget?.name}" 검사를 삭제할까요? 이 작업은 되돌릴 수 없습니다.`}
          confirmLabel="삭제" danger
          onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)} />
      </div>

      {/* ── Right: selected test info ─────────────────────────── */}
      <div className="hidden lg:flex w-[320px] shrink-0 flex-col border-l border-gray-100 bg-gray-50 overflow-y-auto p-6">
        {selectedTest ? (
          <>
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-bold text-gray-900">{selectedTest.name}</p>
                {selectedTest.is_active && <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">액티브</span>}
              </div>
              <p className="text-xs text-gray-400 mb-1">수정일: {new Date(selectedTest.updated_at).toLocaleString("ko-KR")}</p>
              <p className="text-xs text-gray-500 mb-3">
                공개 URL: <span className="font-medium text-gray-700">{PUBLIC_BASE}/t/{selectedTest.slug}</span>
                <button onClick={() => navigator.clipboard.writeText(`${PUBLIC_BASE}/t/${selectedTest.slug}`).catch(() => {})}
                  className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] hover:bg-gray-200">복사</button>
              </p>
              <div className="flex gap-2">
                <Link to={tenantPath(`tests/${selectedTest.id}/edit`)}
                  className="flex-1 rounded-xl bg-brand-500 px-3 py-2 text-center text-xs font-semibold text-white hover:bg-brand-600">편집하기</Link>
                <a href={`${PUBLIC_BASE}/t/${selectedTest.slug}`} target="_blank" rel="noopener noreferrer"
                  className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-white">미리보기 ↗</a>
              </div>
            </div>
            <div className="space-y-1 text-xs text-gray-500">
              <p>개인정보 수집: {selectedTest.collect_personal_info ? "예" : "아니오"}</p>
              <p className="font-mono text-gray-300 break-all">{selectedTest.id}</p>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-gray-300 text-center">← 검사를 선택하면<br/>정보가 표시됩니다</p>
          </div>
        )}
      </div>
    </div>
  );
}

